import surroundedByIdiotsCover from '../../assets/surroundedbyidiots.jpg';

import redImage from '../../assets/red.png';
import yellowImage from '../../assets/yellow.png';
import greenImage from '../../assets/green.png';
import blueImage from '../../assets/blue.png';

const surroundedByIdiots = {
  slug: "surrounded-by-idiots",
  title: "Surrounded by Idiots",
  author: "Thomas Erikson",
  cover: surroundedByIdiotsCover,
  dateRead: "August 2025",
  categories: ["Social", "Life", "Self-Awareness"],
  mediumLink: "https://medium.com/@Loubna-DS/on-surrounded-by-idiots-by-thomas-erikson-fbde23143267",
  contentSections: [
    {
      id: "why-i-read",
      title: "📖 Why I Read This Book",
      content: [
        {
          type: "markdown",
          value: `
As much as we try to ignore it, we’re social beings. We thrive on connection.  

For most of my life, I focused on developing my technical skills — the ones tied to my career — and neglected the equally important skill of understanding and communicating with people.

At some point, I realized that the world doesn’t only need people who are good at their jobs; it needs people who know how to connect, collaborate, and communicate.

Most of the time, we think we’re great and the problem is with other people — not us.  
We think we are *surrounded by idiots*.

That’s why the title instantly caught my attention. At first, it was an ego thing — I thought I was right.  

But after reading the book, my perspective shifted completely.  
People aren’t difficult — **they’re just different**.
          `
        }
      ]
    },
    {
      id: "three-words",
      title: "🧠 The Book in 3 Words",
      content: [
        {
          type: "markdown",
          value: `
**Structured. Relatable. Complex.**

The book breaks human behavior into clear patterns you can instantly recognize — yet constantly reminds you that people are far more nuanced than any label.

You start seeing patterns everywhere… and often think: *That’s me. I’ve done that.*
          `
        }
      ]
    },
    {
      id: "one-insight",
      title: "💡 One Insight That Shifted Me",
      content: [
        {
          type: "markdown",
          value: `
**“Act like yourself” won’t always help you build meaningful connections.**

I used to believe being myself was the golden rule — take it or leave it.  
This book challenged that belief.

It’s not about pretending.  
It’s about **awareness** — about meeting people where *they* are, not where you wish they were.

Sometimes, being flexible is what creates fewer conflicts, smoother communication, and deeper relationships.
          `
        }
      ]
    },
    {
      id: "favorite-quotes",
      title: "❝ My Favorite Quotes",
      content: [
        {
          type: "markdown",
          value: `
> *“Communication happens on the listener’s terms.”*

> *“Behavior is a function of personality and surrounding factors.”*

> *“We see what we do, but we don’t see why we do what we do.”*
          `
        }
      ]
    },
    {
      id: "summary-notes",
      title: "🧾 Summary & Notes",
      content: [
        {
          type: "markdown",
          value: `
**Effective communication = adjusting yourself to how others want to be treated.**

Most of us communicate based on our own preferences.  
But successful communication requires flexibility, empathy, and adaptation.

You can be fully yourself when you’re alone — or with someone just like you.  
Everyone else requires awareness and adjustment.

---

## 🧠 The DISC Model

**Behaviour = F(Personality × Surrounding Factors)**

The DISC system identifies four main behavior types:

- 🔴 **Dominance (Red)**
- 🟡 **Inducement (Yellow)**
- 🟢 **Submission (Green)**
- 🔵 **Compliance (Blue)**

![DISC overview](https://miro.medium.com/v2/resize:fit:1400/format:webp/1*1l8zGnTTPWigX_HFnXp8Mw.png)

---

### 🔴 Red — Dominance
Red personalities can’t sit still — they’re highly competitive, ambitious, and believe nothing is impossible.  
They dream big, chase results, and often see their way as the only way.

**Typical Red might say:**  
*"I try to care about you, but it would help if you were a little more interesting."*

**Body language:**  
Keeps distance, powerful handshake, leans forward aggressively, direct eye contact, strong gestures.

**How to adapt:**  
Cut the small talk. Be direct, clear, and focused on results.

![Red personality](${redImage})

---

### 🟡 Yellow — Inducement
Yellows are optimistic, social, and energetic.  
They thrive on connection but can struggle with focus and listening.

**Typical Yellow might say:**  
*"Look to juggle all the balls at the same time."*

**Body language:**  
Tactile, friendly eye contact, expressive gestures, physically close, open and engaging.

**How to adapt:**  
Create a warm atmosphere. Ask how things *feel*. Let them talk.

![Yellow personality](${yellowImage})

---

### 🟢 Green — Submission
Greens are loyal, calm, supportive, and conflict-averse.  
They avoid confrontation and resist sudden change.

**Typical Green might say:**  
*"I know I should change this immediately, but I’ll just think about it for a while."*

**Body language:**  
Relaxed, comfortable distance, gentle eye contact, soft voice, slow gestures.

**How to adapt:**  
Offer reassurance and gently encourage them out of their comfort zone.

![Green personality](${greenImage})

---

### 🔵 Blue — Compliance
Blues are analytical, precise, and value correctness above all.  
They are detail-oriented, logical, and often perfectionists.

**Typical Blue might say:**  
*"The only thing I can trust is myself and my own eyes."*

**Body language:**  
Closed posture, minimal gestures, direct eye contact, prefers space and solitude.

**How to adapt:**  
Be prepared, factual, patient, and don’t rush them. Provide structure and logic.

![Blue personality](${blueImage})

---

## 🌱 Final Reflection

After reading this book, I realized I’m mostly **Green**, with strong **Blue** tendencies.  
It helped me understand not only myself, but also the people around me — at work, in family, and in relationships.

This book doesn’t just explain people.  
It teaches you how to be **more considerate**, which can change everything.
          `
        }
      ]
    }
  ]
};

export default surroundedByIdiots;
